import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { CaseService, StudyService } from '@/services';
import { useStudyStore } from '@/stores/study';
import { CaseModel, type PostLockUnlockResource, LoadingStateModel } from '@/models';
import type { CaseMemberInput } from '@/models/caseMember';
import { TableColumnType, type TableColumnModel } from '@/components/common/table/interfaces/TableColumnModel';
import type { PaginatorData } from '@/components/common/interfaces/PaginatorData';
import { DATA_VIEW_FILTER, LocalStorageUtils } from '@/utils';
import { useEntryListStore } from '@/stores/entryList';
import { usePipelineStore } from '@/stores/pipeline';
import { useRoute } from 'vue-router';


export const useCaseStore = defineStore('case', () => {

  const tableColumns: TableColumnModel[] = [
    {
      id: 1,
      header: 'Case Label',
      key: 'label',
      type: TableColumnType.TEXT_WTIH_ICON,
      icon: {
        icon: 'folder',
        style: 'fas',
        color: 'rgb(133, 198, 255)'
      },
      sortable: true
    },
    {
      id: 2,
      header: 'Creation Date',
      type: TableColumnType.TEXT,
      key: 'createdAt',
      sortable: true
    },
    {
      id: 3,
      header: 'Created By',
      type: TableColumnType.USER,
      key: 'creatorNames',
      sortable: true
    }
  ];

  const route = useRoute();
  const studyStore = useStudyStore();
  const entryListStore = useEntryListStore();
  const pipelineStore = usePipelineStore();
  const caseList = ref<CaseModel[]>([]);
  const studyCaseList = ref<CaseModel[]>([]);
  const totalCasesCount = ref<number>(0);
  const currentPage = ref(1);
  const pageLimit = ref(25);
  const searchText = ref('');
  const sortField = ref<string | undefined>('createdAt');
  const sortOrder = ref<string | undefined>('DESC');

  const isPumpStudy = computed(() => studyStore.selectedStudy?.flags?.customDataDictionary === 'PUMP');

  const casesLoader = ref(new LoadingStateModel({isLoading: false}));
  const getCases = async (caseId?: string) => {
    casesLoader.value.startLoading();
    try {
      const caseResponse = await CaseService.getCases(studyStore.selectedStudyId!, currentPage.value, 
        pageLimit.value, searchText.value, undefined, sortField.value, sortOrder.value);
      caseList.value = caseResponse.cases;
      totalCasesCount.value = caseResponse.casesCount;
      if (caseId) {
        setEntryListStoreCase(caseList.value, caseId);
      }
    } catch (error) {
      casesLoader.value.setError(error);
    } finally {
      casesLoader.value.stopLoadingAndFinish();
    }
  };

  const setSort = (sortBy?: string) => {
    if (sortBy != sortField.value) {
      sortField.value = sortBy;
      getCases();
    }
  };

  const setSortOrder = (order?: number) => {
    if (order != sortOrder.value && order !== null) {
      sortOrder.value = order === 1 ? 'ASC' : 'DESC';
      getCases();
    }
  };

  const onPageChange = (pageData: PaginatorData) => {
    currentPage.value = pageData.page + 1;
    pageLimit.value = pageData.rows;
    getCases();
  };

  const handleCaseSearch = (searchQuery: string) => {
    currentPage.value = 1;
    searchText.value = searchQuery;
    getCases();
  };

  const getStudyCases = async (archive?: boolean, caseId?: string, whatFor?: string) => {
    casesLoader.value.startLoading();
    try {
      if (route.params.studyIdentifier) {
        studyStore.selectedStudyId = route.params.studyIdentifier as string;
      }
      const caseResponse = await CaseService.getStudyCases(studyStore.selectedStudyId!, archive, whatFor);
      studyCaseList.value = caseResponse.cases.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
      totalCasesCount.value = caseResponse.casesCount;
      if (caseId) {
        setEntryListStoreCase(studyCaseList.value, caseId);
      }
    } catch (error) {
      casesLoader.value.setError(error);
    } finally {
      casesLoader.value.stopLoadingAndFinish();
    }
  };

  const caseActionLoader = ref(new LoadingStateModel({isLoading: false}));
  const lockUnlockCases = async (selectedCases: CaseModel[], status: string) => {
    caseActionLoader.value.startLoading();
    const postPayload: PostLockUnlockResource = {
      cases: selectedCases.map(caseItem => ({
        identifier: caseItem.identifier,
        revisionIdentifier: caseItem.identifier
      })),
      status
    };
    try {
      if (status === 'locked' || status === 'unlocked') {
        await StudyService.lockUnlockResources(studyStore.selectedStudyId!, postPayload);
        caseActionLoader.value.setSuccess(`Selected cases have been ${status}`);
      } else {
        await StudyService.archiveResources(studyStore.selectedStudyId!, postPayload);
        caseActionLoader.value.setSuccess(`Selected cases have been archived`);
      }
      if (LocalStorageUtils.getCurrentDataViewFilter(studyStore.selectedStudyId!) === DATA_VIEW_FILTER.PIPELINE) {
        pipelineStore.getPipelineCases();
      } else {
        getCases();
      }
    } catch (error) {
      caseActionLoader.value.setError(error);
    } finally {
      caseActionLoader.value.stopLoadingAndFinish();
    }
  };

  const handleCaseRestore = async () => {
    caseActionLoader.value.startLoading();
    const postPayload: PostLockUnlockResource = {
      cases: [{
        identifier: entryListStore.selectedCase!.identifier,
        revisionIdentifier: entryListStore.selectedCase!.identifier
      }],
      status: 'active'
    };
    
    try {
      await StudyService.archiveResources(studyStore.selectedStudyId!, postPayload);
      caseActionLoader.value.setSuccess(`The case has been restored.`);
      delete entryListStore.selectedCase?.archivedAt;
    } catch (error) {
      caseActionLoader.value.setError(error);
    } finally {
      caseActionLoader.value.stopLoadingAndFinish();
    }
  };

  const createCase = async (members?: CaseMemberInput[]) => {
    caseActionLoader.value.startLoading();
    try {
      const caseData = await CaseService.createCase(
        studyStore.selectedStudyId!,
        studyStore.selectedStudy?.flags,
        members
      );
      await getCases();
      caseActionLoader.value.setSuccess('New case created successfully');
      return caseData;
    } catch (error) {
      caseActionLoader.value.setError(error);
    } finally {
      caseActionLoader.value.stopLoadingAndFinish();
    }
  };

  const updateCase = async (caseId: string, caseName: string) => {
    caseActionLoader.value.startLoading();
    let caseUpdated = false;
    try {
      await CaseService.updateCase(caseId, caseName);
      caseActionLoader.value.setSuccess('Case updated successfully');
      caseUpdated = true;
    } catch (error) {
      caseActionLoader.value.setError(error);
    } finally {
      caseActionLoader.value.stopLoadingAndFinish();
    }
    return caseUpdated;
  };

  const setSelectedCase = (path: string, caseId?: string) => {
    //using the path instead of route name, since there are couple of different route which has /data/case in their paths
    if (path.includes('/data/case') && caseId && !entryListStore.selectedCase) {
      if (caseList.value.length > 0) {
        setEntryListStoreCase(caseList.value, caseId);
      } else if (studyCaseList.value.length > 0) {
        setEntryListStoreCase(studyCaseList.value, caseId);
      } else {
        entryListStore.selectedCase = undefined;
      }
    }
  };

  const setEntryListStoreCase = (casesList: CaseModel[], caseId: string) => {
    entryListStore.selectedCase = casesList.find(
      caseItem => caseItem.identifier === caseId
    );
  };

  return {
    getCases,
    onPageChange,
    lockUnlockCases,
    getStudyCases,
    createCase,
    updateCase,
    handleCaseSearch,
    setSelectedCase,
    handleCaseRestore,
    setSort,
    setSortOrder,
    searchText,
    tableColumns,
    caseList,
    studyCaseList,
    totalCasesCount,
    currentPage,
    pageLimit,
    casesLoader,
    caseActionLoader,
    isPumpStudy
  };
});
